import React, {
  ReactNode,
  createContext,
  useContext,
  useRef,
  useState,
} from "react";
import { Modal, ModalContent } from "./Modal";
import DynamicHeroIcon, { HeroIcons } from "./DynamicHeroIcon";
import { createPortal } from "react-dom";
import { ButtonSolid } from "./Button";

export interface ConfirmationOptions {
  icon?: HeroIcons;
  message: string | ReactNode;
  showIcon: boolean;
  intent: "default" | "danger";
}

export type ConfirmationState = {
  id: string;
} & ConfirmationOptions;

export type ConfirmationContextProps = (
  opt: ConfirmationOptions
) => Promise<boolean>;

export const ConfirmationContext =
  createContext<ConfirmationContextProps | null>(null);

export interface ConfirmationProviderProps {
  children: ReactNode;
}

export const useConfirmation = () => useContext(ConfirmationContext);

type ConfirmationResolver = (state: PromiseLike<boolean> | boolean) => void;

export const ConfirmationProvider = ({
  children,
}: ConfirmationProviderProps) => {
  const [modalState, setModalState] = useState(false);
  const resolver = useRef<ConfirmationResolver>();
  const [message, setMessage] = React.useState<string | ReactNode>(
    "Are you sure you want to delete?"
  );
  const [intent, setIntent] = React.useState<"danger" | "default">("default");
  const confirm = (opt: ConfirmationOptions) => {
    setModalState(true);
    setIntent(opt.intent);
    setMessage(opt.message);
    return new Promise<boolean>(function (resolve) {
      resolver.current = resolve;
    });
  };

  const handleConfirm = () => {
    resolver && resolver.current && resolver.current(true);
    setModalState(false);
  };

  const handleCancel = () => {
    resolver && resolver.current && resolver.current(false);
    setModalState(false);
  };

  const contextValue = React.useMemo(() => confirm, []);

  return (
    <ConfirmationContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <>
          <ConfirmationModal
            intent={intent}
            message={message}
            showIcon={true}
            cancelCallback={handleCancel}
            confirmationCallback={handleConfirm}
            open={modalState}
            onOpenChange={setModalState}
          />
        </>,
        document.body
      )}
    </ConfirmationContext.Provider>
  );
};

interface ConfirmationModalProps extends ConfirmationOptions {
  confirmationCallback: () => void;
  cancelCallback: () => void;
  open: boolean;
  onOpenChange: (state: boolean) => void;
}

const ConfirmationModal = ({
  confirmationCallback,
  cancelCallback,
  message,
  intent,
  open,
  onOpenChange,
}: ConfirmationModalProps) => {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="relative h-auto w-auto max-w-2xl rounded-lg bg-white p-4 shadow dark:bg-gray-900">
        <div className="flex flex-col gap-8">
          <div className="flex flex-row-reverse items-center">
            <button type="button" onClick={cancelCallback}>
              <DynamicHeroIcon
                icon="XMarkIcon"
                className="h-6 w-6 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
              />
            </button>
          </div>
          <div className="flex flex-col items-center justify-center gap-4 px-10">
            <DynamicHeroIcon
              icon="ExclamationCircleIcon"
              type="outline"
              className="h-12 w-12 text-gray-400 "
            />
            {typeof message === "string" ? (
              <div className="text-lg text-gray-600 dark:text-gray-400">
                {message}
              </div>
            ) : (
              message
            )}
          </div>
          <div className="flex flex-row items-center justify-center gap-4">
            <ButtonSolid
              label={"Yes, I'm sure"}
              variant={intent === "danger" ? "red" : "default"}
              onClick={confirmationCallback}
            />
            <ButtonSolid
              label={"No, cancel"}
              variant="alternative"
              onClick={cancelCallback}
            />
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ConfirmationModal;
